/**
 * usePlatformConfig Hook
 *
 * Provides platform configuration management using real Firestore.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  orderBy,
  getDocs,
  addDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import { useAuth } from '@/contexts';

export type FeeMode = 'host_pays' | 'guest_pays' | 'split';

export interface PlatformConfig {
  fees: {
    platformCommission: {
      type: 'percentage';
      value: number; // e.g. 10 for 10%
      mode: FeeMode; // who pays the commission (default: host_pays)
    };
  };
  payouts: {
    minimumThreshold: number;
    wireTransferFee: number;
    whishMoneyFee: number;
  };
  payments: {
    /** When true (and merchant secrets are set), guests pay Whish via the
     * hosted Whish Pay page instead of manual transfer instructions. */
    whishPayEnabled: boolean;
  };
  defaults: {
    currency: string;
    cancellationPolicyId: string;
  };
  updatedAt: Date;
  updatedBy: string;
}

export interface ConfigHistoryEntry {
  id: string;
  previousConfig: Partial<PlatformConfig>;
  newConfig: Partial<PlatformConfig>;
  reason: string;
  changedBy: string;
  changedByName: string;
  changedAt: Date;
}

const DEFAULT_CONFIG: PlatformConfig = {
  fees: {
    platformCommission: { type: 'percentage', value: 10, mode: 'host_pays' },
  },
  payouts: { minimumThreshold: 50, wireTransferFee: 15, whishMoneyFee: 0 },
  payments: { whishPayEnabled: false },
  defaults: { currency: 'USD', cancellationPolicyId: 'flexible' },
  updatedAt: new Date(),
  updatedBy: '',
};

const toDate = (val: any): Date => {
  if (val instanceof Timestamp) return val.toDate();
  if (val instanceof Date) return val;
  return val ? new Date(val) : new Date();
};

export const usePlatformConfig = () => {
  const [config, setConfig] = useState<PlatformConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    let cancelled = false;

    const fetchConfig = async () => {
      setIsLoading(true);
      try {
        const docSnap = await getDoc(doc(db, 'platformConfig', 'current'));
        if (!cancelled) {
          if (docSnap.exists()) {
            const data = docSnap.data();
            // Backfill the new `mode` field for older configs that pre-date it.
            const fees = data.fees || DEFAULT_CONFIG.fees;
            if (fees?.platformCommission && !fees.platformCommission.mode) {
              fees.platformCommission.mode = 'host_pays';
            }
            setConfig({
              fees,
              payouts: data.payouts || DEFAULT_CONFIG.payouts,
              payments: data.payments || DEFAULT_CONFIG.payments,
              defaults: data.defaults || DEFAULT_CONFIG.defaults,
              updatedAt: toDate(data.updatedAt),
              updatedBy: data.updatedBy || '',
            });
          } else {
            // Initialize with defaults
            setConfig(DEFAULT_CONFIG);
          }
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch platform config:', err);
        if (!cancelled) {
          setConfig(DEFAULT_CONFIG);
          setIsLoading(false);
        }
      }
    };

    fetchConfig();
    return () => { cancelled = true; };
  }, []);

  const updateConfig = useCallback(
    async (newConfig: PlatformConfig, reason: string) => {
      try {
        const updatedConfig = {
          ...newConfig,
          updatedAt: new Date(),
          updatedBy: user?.uid || '',
        };

        // Save new config
        await setDoc(doc(db, 'platformConfig', 'current'), {
          fees: updatedConfig.fees,
          payouts: updatedConfig.payouts,
          payments: updatedConfig.payments || { whishPayEnabled: false },
          defaults: updatedConfig.defaults,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || '',
        });

        // Add to history
        await addDoc(collection(db, 'platformConfig', 'current', 'history'), {
          previousConfig: config,
          newConfig: updatedConfig,
          reason,
          changedBy: user?.uid || '',
          changedByName: user?.displayName || 'Admin',
          changedAt: serverTimestamp(),
        });

        // No auditLogs write here: that collection is server-only by design
        // (firestore.rules `allow create: if false`) so entries can't be
        // forged by a client. Attempting it threw permission-denied on every
        // save, after the config had already been written — making a
        // successful save look like a failure. The `history` subcollection
        // above already records who changed what, when, and why.

        setConfig(updatedConfig);
        return updatedConfig;
      } catch (err) {
        console.error('Failed to update platform config:', err);
        throw err;
      }
    },
    [config, user]
  );

  return {
    config,
    isLoading,
    updateConfig,
  };
};

export const useConfigHistory = () => {
  const [history, setHistory] = useState<ConfigHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchHistory = async () => {
      setIsLoading(true);
      try {
        const q = query(
          collection(db, 'platformConfig', 'current', 'history'),
          orderBy('changedAt', 'desc')
        );
        const snapshot = await getDocs(q);

        if (!cancelled) {
          setHistory(
            snapshot.docs.map((docSnap) => {
              const data = docSnap.data();
              return {
                id: docSnap.id,
                previousConfig: data.previousConfig || {},
                newConfig: data.newConfig || {},
                reason: data.reason || '',
                changedBy: data.changedBy || '',
                changedByName: data.changedByName || 'Admin',
                changedAt: toDate(data.changedAt),
              };
            })
          );
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch config history:', err);
        if (!cancelled) {
          setHistory([]);
          setIsLoading(false);
        }
      }
    };

    fetchHistory();
    return () => { cancelled = true; };
  }, []);

  return {
    history,
    isLoading,
  };
};

export interface FeeCalculation {
  platformCommission: number;
  guestTotal: number;
  hostPayout: number;
  platformRevenue: number;
}

export const calculateFees = (
  bookingAmount: number,
  config: PlatformConfig
): FeeCalculation => {
  // Platform commission is deducted from the host's share - guests pay no extra fee
  const platformCommission = bookingAmount * (config.fees.platformCommission.value / 100);

  const guestTotal = bookingAmount; // No service fee added for guests
  const hostPayout = bookingAmount - platformCommission;
  const platformRevenue = platformCommission;

  return { platformCommission, guestTotal, hostPayout, platformRevenue };
};

export default usePlatformConfig;
